/**
 * RouteMap — Google-Maps-based view of the courier's daily route.
 *
 * Loads the Google Maps JS lazily (only when this component mounts), drops
 * numbered pins (1, 2, 3, ...) for each stop in current route order,
 * draws a polyline through them, and fits the viewport to the route bounds.
 *
 * Click a pin to open a bottom-sheet with stop info and quick actions
 * (open in nav app via a `geo:` URL, open the order detail screen).
 *
 * Drag-to-reorder on the map is intentionally NOT implemented on the mobile
 * PWA — touch-and-hold drag on the list tab is the primary reorder UX on
 * phone (per Track E brief). Map shows the current order; list owns reorder.
 *
 * Tap targets are >= 44 x 44 px on every map control.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLocationArrow, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';

import {
    loadGoogleMaps,
    isGoogleMapsKeyConfigured,
    MapsLoadError,
} from '../services/googleMapsLoader';
import { ProcessedOrder, OrderCoordinates, Warehouse } from '../types/logistics';

export interface RouteMapProps {
    stops: ProcessedOrder[];
    orderCoordinates: Record<number, OrderCoordinates> | null;
    warehouse: Warehouse | null;
}

interface PinModel {
    routeIndex: number; // 1-based label
    stop: ProcessedOrder;
    lat: number;
    lng: number;
}

const RouteMap = ({ stops, orderCoordinates, warehouse }: RouteMapProps) => {
    const mapElRef = useRef<HTMLDivElement | null>(null);
    // Use `unknown` to avoid pulling in the @types/google.maps dep (not in
    // the repo); we cast at the boundary when invoking maps APIs.
    const mapRef = useRef<unknown>(null);
    const overlaysRef = useRef<{ markers: unknown[]; polyline: unknown | null }>({
        markers: [],
        polyline: null,
    });

    const [loadError, setLoadError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [activePin, setActivePin] = useState<PinModel | null>(null);

    const navigate = useNavigate();

    const pins = useMemo<PinModel[]>(() => {
        const list: PinModel[] = [];
        stops.forEach((stop, idx) => {
            const coords = orderCoordinates?.[stop.id];
            const lat = stop.type === 'collection' ? coords?.collectionLatitude : coords?.deliveryLatitude;
            const lng = stop.type === 'collection' ? coords?.collectionLongitude : coords?.deliveryLongitude;
            if (lat != null && lng != null) {
                list.push({ routeIndex: idx + 1, stop, lat, lng });
            }
        });
        return list;
    }, [stops, orderCoordinates]);

    // Initialise the map once the container is mounted + the Maps JS API is loaded.
    useEffect(() => {
        let cancelled = false;

        if (!isGoogleMapsKeyConfigured()) {
            setLoadError(
                'Map view is unavailable: the Google Maps key has not been configured for this build.'
            );
            setLoading(false);
            return;
        }

        loadGoogleMaps()
            .then((mapsNs) => {
                if (cancelled) return;
                const maps = mapsNs as any;
                if (!mapElRef.current) return;

                const map = new maps.Map(mapElRef.current, {
                    center: { lat: 51.5074, lng: -0.1278 }, // London fallback; refit below
                    zoom: 9,
                    disableDefaultUI: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    gestureHandling: 'greedy',
                });
                mapRef.current = map;
                setLoading(false);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const msg =
                    err instanceof MapsLoadError
                        ? err.message
                        : 'Map could not be loaded. Please check your connection and try again.';
                setLoadError(msg);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Re-render markers and polyline whenever the route changes.
    useEffect(() => {
        const map: any = mapRef.current;
        if (!map || !window.google?.maps) return;
        const maps: any = window.google.maps;

        // Tear down old overlays.
        overlaysRef.current.markers.forEach((m: any) => m.setMap?.(null));
        overlaysRef.current.markers = [];
        if (overlaysRef.current.polyline) {
            (overlaysRef.current.polyline as any).setMap?.(null);
            overlaysRef.current.polyline = null;
        }

        if (pins.length === 0) {
            // Centre on warehouse if we have it; else leave the default fallback.
            if (warehouse?.latitude != null && warehouse?.longitude != null) {
                map.setCenter({ lat: warehouse.latitude, lng: warehouse.longitude });
                map.setZoom(11);
            }
            return;
        }

        const bounds = new maps.LatLngBounds();
        const path: { lat: number; lng: number }[] = [];

        if (warehouse?.latitude != null && warehouse?.longitude != null) {
            const whLat = warehouse.latitude;
            const whLng = warehouse.longitude;
            const whMarker = new maps.Marker({
                position: { lat: whLat, lng: whLng },
                map,
                title: warehouse.name || 'Warehouse',
                label: { text: 'W', color: '#fff', fontWeight: '700' },
                icon: {
                    path: maps.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: '#27272b',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
            });
            overlaysRef.current.markers.push(whMarker);
            bounds.extend({ lat: whLat, lng: whLng });
            path.push({ lat: whLat, lng: whLng });
        }

        pins.forEach((pin) => {
            const marker = new maps.Marker({
                position: { lat: pin.lat, lng: pin.lng },
                map,
                title: `${pin.routeIndex}. ${pin.stop.type === 'collection' ? 'Collect' : 'Deliver'}`,
                label: { text: String(pin.routeIndex), color: '#fff', fontWeight: '700' },
                icon: {
                    path: maps.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: pin.stop.type === 'collection' ? '#6c3cd9' : '#22c55e',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
            });
            marker.addListener('click', () => setActivePin(pin));
            overlaysRef.current.markers.push(marker);
            bounds.extend({ lat: pin.lat, lng: pin.lng });
            path.push({ lat: pin.lat, lng: pin.lng });
        });

        // Polyline follows route order; warehouse is start (and conceptually end).
        if (path.length >= 2) {
            const polyline = new maps.Polyline({
                path,
                strokeColor: '#6c3cd9',
                strokeOpacity: 0.85,
                strokeWeight: 4,
                map,
            });
            overlaysRef.current.polyline = polyline;
        }

        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, { top: 24, right: 24, bottom: 24, left: 24 });
        }
    }, [pins, warehouse]);

    if (loadError) {
        return (
            <div className="route-map-fallback" role="alert">
                <p>{loadError}</p>
                <p className="hint">Use the List view tab to see your stops.</p>
            </div>
        );
    }

    return (
        <div className="route-map-container">
            <div className="route-map-canvas" ref={mapElRef} aria-label="Route map" />
            {loading && (
                <div className="route-map-loading" role="status">
                    Loading map...
                </div>
            )}
            {pins.length === 0 && !loading && (
                <div className="route-map-empty">
                    No stop coordinates available. Switch to the List view to see your stops.
                </div>
            )}
            {activePin && (
                <RouteMapBottomSheet
                    pin={activePin}
                    onClose={() => setActivePin(null)}
                    onOpenOrder={() => {
                        const type = activePin.stop.type;
                        navigate(`/courier/order/${activePin.stop.id}/${type}`);
                    }}
                />
            )}
        </div>
    );
};

interface BottomSheetProps {
    pin: PinModel;
    onClose: () => void;
    onOpenOrder: () => void;
}

const RouteMapBottomSheet = ({ pin, onClose, onOpenOrder }: BottomSheetProps) => {
    const { stop, lat, lng, routeIndex } = pin;
    const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    const customerName = stop.customer
        ? `${stop.customer.firstName} ${stop.customer.lastName}`
        : `Order #${stop.id}`;
    const address = stop.type === 'collection' ? stop.collectionAddress : stop.deliveryAddress;

    return (
        <div className="route-map-sheet" role="dialog" aria-label={`Stop ${routeIndex} details`}>
            <button
                type="button"
                className="sheet-close"
                onClick={onClose}
                aria-label="Close stop details"
            >
                <FaTimes />
            </button>
            <div className="sheet-header">
                <span className="sheet-step">Stop {routeIndex}</span>
                <span className={`sheet-type ${stop.type}`}>
                    {stop.type === 'collection' ? 'Collect' : 'Deliver'}
                </span>
            </div>
            <div className="sheet-body">
                <strong>{customerName}</strong>
                <span>{address}</span>
            </div>
            <div className="sheet-actions">
                <a
                    className="sheet-btn"
                    href={navUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open directions in Google Maps"
                >
                    <FaLocationArrow />
                    <span>Navigate</span>
                </a>
                <button type="button" className="sheet-btn primary" onClick={onOpenOrder}>
                    <FaExternalLinkAlt />
                    <span>Open order</span>
                </button>
            </div>
        </div>
    );
};

export default RouteMap;
